import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnvelopeSimple, IdentificationCard, ClipboardText, UserPlus, MagnifyingGlass, Link as LinkIcon } from "@phosphor-icons/react";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { d, dialerError, fmtDateTime, fmtPhone, notifyDialerChanged } from "../../../lib/dialer";
import NewClientDialog from "./NewClientDialog";

/** Cliente de la llamada: vincular por ID, alta, notas, órdenes e historial de correo. Nunca envía correo. */
export default function BubbleClientPanel({ call, role, canAct, notify, prefillRef }) {
  const navigate = useNavigate();
  const [ctxData, setCtxData] = useState(null);
  const [ctxErr, setCtxErr] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [note, setNote] = useState("");
  const isBilingue = role === "bilingue";
  const isObserver = role === "observer";  // bilingüe pareja en VERDE: ficha por ID, notas e historial; sin correo ni vínculo por teléfono

  // Historial/órdenes/notas: se vuelve a pedir (y a validar en el servidor) en cada lectura.
  const loadCtx = useCallback(async () => {
    if (isBilingue) return;
    try { const r = await d.get(`/calls/${call.id}/context`); setCtxData(r.data); setCtxErr(""); }
    catch (e) { setCtxData(null); setCtxErr(dialerError(e)); }
  }, [call.id, isBilingue]);
  useEffect(() => { loadCtx(); }, [loadCtx, call.client_id, call.client_version]);

  // Datos confirmados de la consulta externa: abren el alta ya precargada (la persona guarda).
  useEffect(() => {
    if (prefillRef) prefillRef.current = (data) => { setPrefill(data); setNewOpen(true); };
    return () => { if (prefillRef) prefillRef.current = null; };
  }, [prefillRef]);

  const search = async () => {
    setBusy(true);
    try { const r = await d.get("/clients/search", { params: { q } }); setRows(r.data); if (!r.data.length) notify.error("Sin resultados."); }
    catch (e) { notify.error(dialerError(e)); } finally { setBusy(false); }
  };
  const link = async (clientId) => {
    setBusy(true);
    try {
      await d.post(`/calls/${call.id}/link-client`, { ...(clientId ? { client_id: clientId } : {}), expected_version: call.client_version || 0 });
      setRows([]); notifyDialerChanged(); notify.success("Llamada vinculada por ID de cliente.");
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.code === "ambiguo") notify.error("Varias fichas con este número: búscala y elígela.");
      else notify.error(dialerError(e));
    } finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim() || !call.client_id) return;
    setBusy(true);
    try { await api.post(`/clients/${call.client_id}/notes`, { content: note.trim() }); setNote(""); await loadCtx(); notify.success("Nota guardada."); }
    catch (e) { notify.error(dialerError(e)); } finally { setBusy(false); }
  };

  if (isBilingue) return <div className="text-xs text-muted-foreground p-3" data-testid="bubble-client-bilingue">La ficha de esta llamada la gestiona el asistente o un supervisor; tú sigues con tu panel de captura.</div>;

  return (
    <div className="space-y-3 text-sm" data-testid="bubble-client-panel">
      {call.client_id ? (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 space-y-2" data-testid="bubble-linked">
          <div className="font-semibold" data-testid="bubble-linked-name">{call.client_name}</div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate(`/clientes/${call.client_id}`)} data-testid="bubble-open-client"><IdentificationCard size={14} className="mr-1" /> Ver ficha</Button>
            {/* Abre el módulo de correo con el cliente precargado. NUNCA envía. */}
            {!isObserver && <Button size="sm" variant="outline" onClick={() => navigate(`/enviar?client=${encodeURIComponent(call.client_id)}`)} data-testid="bubble-open-email" title="Abre el módulo de correo con el cliente ya elegido; no envía nada"><EnvelopeSimple size={14} className="mr-1" /> Preparar correo</Button>}
            <Button size="sm" variant="outline" onClick={() => navigate("/ordenes")} data-testid="bubble-open-orders"><ClipboardText size={14} className="mr-1" /> Órdenes</Button>
          </div>
          <div className="text-[11px] text-muted-foreground">“Preparar correo” solo abre el módulo; nada se envía hasta que lo pulses allí.</div>
        </div>
      ) : (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs space-y-2" data-testid="bubble-unlinked">
          <div>Sin ficha todavía. La transcripción muestra “Cliente”.</div>
          <div className="flex flex-wrap gap-2">
            {!isObserver && <Button size="sm" disabled={!canAct || busy} onClick={() => link(null)} data-testid="bubble-link-phone"><LinkIcon size={14} className="mr-1" /> Vincular por teléfono</Button>}
            <Button size="sm" variant="outline" disabled={!canAct} onClick={() => { setPrefill(null); setNewOpen(true); }} data-testid="bubble-new-client"><UserPlus size={14} className="mr-1" /> Nuevo cliente</Button>
          </div>
        </div>
      )}

      <div className="space-y-1">
        <div className="text-[11px] text-muted-foreground">{call.client_id ? "Cambiar de ficha" : "O buscar otra ficha"} (nombre, teléfono o correo)</div>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && q.trim().length >= 2) search(); }} data-testid="bubble-search-q" />
          <Button size="sm" variant="outline" disabled={busy || q.trim().length < 2} onClick={search} data-testid="bubble-search-go"><MagnifyingGlass size={14} /></Button>
        </div>
        <ul className="space-y-1 max-h-32 overflow-auto">
          {rows.map((c) => (
            <li key={c.id}><button type="button" className="w-full text-left rounded border border-border hover:border-primary/60 px-2 py-1 text-xs" disabled={!canAct || busy} onClick={() => link(c.id)} data-testid={`bubble-pick-${c.id}`}>
              <b>{c.name}</b> <span className="text-muted-foreground">{fmtPhone(c.phone) || c.phone}</span></button></li>
          ))}
        </ul>
      </div>

      {call.client_id && (
        <div className="space-y-2" data-testid="bubble-context">
          {ctxErr && <div className="text-xs text-amber-300">{ctxErr}</div>}
          {ctxData && (
            <>
              <div><div className="text-[11px] font-semibold tracking-wide">ÓRDENES</div>
                {ctxData.orders.length === 0 ? <div className="text-xs text-muted-foreground">Sin órdenes.</div> : ctxData.orders.map((o) => (
                  <div key={o.id} className="text-xs">#{o.order_number || "s/n"} {o.equipment_model ? `· ${o.equipment_model}` : ""} {o.assigned_bilingual_name ? `· ${o.assigned_bilingual_name}` : ""}</div>))}
              </div>
              {!isObserver && <div><div className="text-[11px] font-semibold tracking-wide">HISTORIAL DE CORREO</div>
                {ctxData.emails.length === 0 ? <div className="text-xs text-muted-foreground" data-testid="bubble-no-emails">Sin correos.</div> : ctxData.emails.map((e) => (
                  <div key={e.id} className="text-xs" data-testid="bubble-email-row">{fmtDateTime(e.sent_at)} · {e.subject || e.template_name}</div>))}
              </div>}
              <div><div className="text-[11px] font-semibold tracking-wide">NOTAS</div>
                {ctxData.notes.length === 0 ? <div className="text-xs text-muted-foreground">Sin notas.</div> : ctxData.notes.map((n) => (
                  <div key={n.id} className="text-xs border-l-2 border-primary/40 pl-2 my-1" data-testid="bubble-note-row">{n.content}</div>))}
                <div className="flex gap-2 mt-1">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Agregar nota…" data-testid="bubble-note-input" />
                  <Button size="sm" disabled={!canAct || busy || !note.trim()} onClick={addNote} data-testid="bubble-note-save">Guardar</Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      <NewClientDialog open={newOpen} onOpenChange={setNewOpen} call={call} prefill={prefill} notify={notify} onDone={() => loadCtx()} />
    </div>
  );
}
